import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import {
  ShieldCheck, CheckCircle2, Clock, XCircle, AlertTriangle,
  Download, Search, ChevronLeft, ChevronRight, Plug,
  RefreshCw, Loader2, FileText,
} from 'lucide-react';

const fmtTS = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit',
    });
  } catch { return s; }
};
const fmtDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return s; }
};

const STATUS_META = {
  Connected: { icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  Pending:   { icon: Clock,        cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  'Pending Agency Onboarding':        { icon: Clock, cls: 'bg-slate-50 text-slate-700 border-slate-200' },
  'Pending Integration Key Provisioning': { icon: Clock, cls: 'bg-slate-50 text-slate-700 border-slate-200' },
  'Pending Bank Credential Activation':   { icon: Clock, cls: 'bg-slate-50 text-slate-700 border-slate-200' },
  Error:     { icon: XCircle,      cls: 'bg-red-50 text-red-800 border-red-200' },
  Pass:        { icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  'In Progress': { icon: Clock,      cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  Fail:        { icon: XCircle,      cls: 'bg-red-50 text-red-800 border-red-200' },
};

export const CompliancePage = () => {
  return (
    <div className="space-y-4" data-testid="compliance-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>
          Compliance &amp; Integrations
        </h1>
        <span className="text-[10px] text-[#9CA3AF] font-mono">Admin / Compliance / Operations only</span>
      </div>

      <IntegrationStatusPanel />
      <FundTaxonomyPanel />
      <FundParityMonitor />
      <WithdrawalPolicyPanel />
      <PayrollAdaptersPanel />
      <ComplianceChecklist />
      <AuditTrail />
    </div>
  );
};

// ─── Withdrawal Policy Config (admin) ────────────────────────────────
const WITHDRAWAL_KINDS = [
  { kind: 'any', label: 'Any (default)' },
  { kind: 'hardship', label: 'Hardship' },
  { kind: 'age_based', label: 'Age-based' },
  { kind: 'in_service_age_59_5', label: 'In-Service (59½)' },
  { kind: 'rmd', label: 'RMD' },
  { kind: 'separation', label: 'Separation' },
];

const WithdrawalPolicyPanel = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const empty = { scope: 'plan', employer_id: '', rules: [{ kind: 'any', max_per_calendar_year: 4, cooldown_days: 0 }], notes: '' };
  const [draft, setDraft] = useState(empty);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/withdrawal-policy')
      .then((r) => setPolicies(r.data || []))
      .catch((e) => setErr(e?.response?.data?.detail || 'Unable to load policies'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addRule = () => {
    const used = draft.rules.map((r) => r.kind);
    const next = WITHDRAWAL_KINDS.find((k) => !used.includes(k.kind))?.kind;
    if (!next) return;
    setDraft({ ...draft, rules: [...draft.rules, { kind: next, max_per_calendar_year: 2, cooldown_days: 0 }] });
  };
  const removeRule = (i) =>
    setDraft({ ...draft, rules: draft.rules.filter((_, idx) => idx !== i) });
  const updateRule = (i, field, val) =>
    setDraft({
      ...draft,
      rules: draft.rules.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)),
    });

  const save = async () => {
    setErr(''); setMsg('');
    if (draft.scope === 'employer' && !draft.employer_id) {
      setErr('employer_id required for employer scope');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        scope: draft.scope,
        employer_id: draft.scope === 'employer' ? draft.employer_id : null,
        rules: draft.rules.map((r) => ({
          kind: r.kind,
          max_per_calendar_year: parseInt(r.max_per_calendar_year, 10) || 0,
          cooldown_days: parseInt(r.cooldown_days, 10) || 0,
        })),
        notes: draft.notes || null,
      };
      await api.put('/admin/withdrawal-policy', payload);
      setMsg('Policy saved.');
      setDraft(empty);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <section className="panel p-5" data-testid="withdrawal-policy-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="overline">Withdrawal Policy (Admin Config)</span>
          <p className="text-[11px] text-[#6B7280] mt-1">
            Caps the number of withdrawals a participant may execute per calendar year. Enforced server-side on submit.
          </p>
        </div>
        <span className="text-[10px] font-mono text-[#9CA3AF]">Plan / Employer Scope</span>
      </div>

      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3" data-testid="wpol-err">{err}</div>}
      {msg && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 mb-3" data-testid="wpol-msg">{msg}</div>}

      {/* Active policies list */}
      <div className="mb-4" data-testid="wpol-list">
        {loading ? (
          <div className="text-xs text-[#6B7280]"><Loader2 size={12} className="animate-spin inline mr-1" />Loading…</div>
        ) : policies.length === 0 ? (
          <div className="text-xs text-[#6B7280]">No policies configured. Creating one below will apply to all participants.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[#9CA3AF] border-b border-[#F0F1F3]">
                <th className="py-1.5">Scope</th>
                <th>Employer</th>
                <th>Rules</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr key={p.id} className="border-b border-[#F0F1F3]" data-testid={`wpol-row-${p.id}`}>
                  <td className="py-2 font-mono uppercase">{p.scope}</td>
                  <td className="font-mono">{p.employer_id || '—'}</td>
                  <td className="font-mono">
                    {p.rules.map((r) => `${r.kind}: ${r.max_per_calendar_year}/yr`).join(' · ')}
                  </td>
                  <td className="font-mono text-[#6B7280]">{fmtTS(p.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Editor */}
      <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3 space-y-3" data-testid="wpol-editor">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-[11px] text-[#6B7280]">Scope</label>
          <select
            value={draft.scope}
            onChange={(e) => setDraft({ ...draft, scope: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
            data-testid="wpol-scope"
          >
            <option value="plan">Plan (all employers)</option>
            <option value="employer">Employer-specific</option>
          </select>
          {draft.scope === 'employer' && (
            <input
              placeholder="Employer ID (e.g. DOD)"
              value={draft.employer_id}
              onChange={(e) => setDraft({ ...draft, employer_id: e.target.value })}
              className="text-xs border border-[#D1D5DB] rounded px-2 py-1 font-mono"
              data-testid="wpol-employer-id"
            />
          )}
        </div>

        <div className="space-y-2">
          {draft.rules.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`wpol-rule-${i}`}>
              <select
                value={r.kind}
                onChange={(e) => updateRule(i, 'kind', e.target.value)}
                className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
              >
                {WITHDRAWAL_KINDS.map((k) => (
                  <option key={k.kind} value={k.kind}>{k.label}</option>
                ))}
              </select>
              <label className="text-[11px] text-[#6B7280]">Max/year</label>
              <input
                type="number" min={0} max={50}
                value={r.max_per_calendar_year}
                onChange={(e) => updateRule(i, 'max_per_calendar_year', e.target.value)}
                className="w-16 text-xs border border-[#D1D5DB] rounded px-2 py-1 font-mono"
              />
              <label className="text-[11px] text-[#6B7280]">Cooldown days</label>
              <input
                type="number" min={0} max={365}
                value={r.cooldown_days}
                onChange={(e) => updateRule(i, 'cooldown_days', e.target.value)}
                className="w-16 text-xs border border-[#D1D5DB] rounded px-2 py-1 font-mono"
              />
              {draft.rules.length > 1 && (
                <button onClick={() => removeRule(i)} className="text-[11px] text-red-600 hover:underline">Remove</button>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={addRule}
            disabled={draft.rules.length >= WITHDRAWAL_KINDS.length}
            className="text-xs border border-[#D1D5DB] rounded px-3 py-1 disabled:opacity-40"
            data-testid="wpol-add-rule"
          >
            + Add type override
          </button>
          <input
            placeholder="Notes (optional)"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            className="flex-1 text-xs border border-[#D1D5DB] rounded px-2 py-1"
          />
          <button
            onClick={save} disabled={saving}
            className="bg-[#1B2A4A] text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-[#0F1A33] disabled:opacity-40"
            data-testid="wpol-save"
          >
            {saving ? 'Saving…' : 'Save Policy'}
          </button>
        </div>
      </div>
    </section>
  );
};

// ─── Payroll Adapter Registry (admin) ────────────────────────────────
const PAYROLL_KINDS = ['federal', 'state', 'county', 'municipal', 'tribal', 'private_employer'];
const PAYROLL_CONN = ['sftp', 'rest', 'soap', 'secure_email'];
const PAYROLL_STATUSES = ['draft', 'pending_legal_review', 'contracted', 'provisioning', 'live', 'suspended', 'deprecated'];

const PayrollAdaptersPanel = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [filterKind, setFilterKind] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const emptyNew = {
    name: '', provider_kind: 'federal', connectivity: 'sftp',
    jurisdiction: '', employer_ids: '', supported_record_types: 'deferrals',
    status: 'draft', contact_email: '', endpoint_url: '', notes: '',
  };
  const [draft, setDraft] = useState(emptyNew);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterKind) params.provider_kind = filterKind;
    if (filterStatus) params.status = filterStatus;
    Promise.all([
      api.get('/admin/payroll-adapters', { params }),
      api.get('/admin/payroll-adapters/summary'),
    ])
      .then(([listRes, sumRes]) => {
        setItems(listRes.data || []);
        setSummary(sumRes.data);
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Unable to load adapters'))
      .finally(() => setLoading(false));
  }, [filterKind, filterStatus]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setErr(''); setMsg('');
    if (!draft.name || draft.name.length < 2) { setErr('Name required'); return; }
    setCreating(true);
    try {
      const payload = {
        name: draft.name,
        provider_kind: draft.provider_kind,
        connectivity: draft.connectivity,
        jurisdiction: draft.jurisdiction || null,
        employer_ids: draft.employer_ids
          ? draft.employer_ids.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        supported_record_types: draft.supported_record_types
          ? draft.supported_record_types.split(',').map((s) => s.trim()).filter(Boolean)
          : ['deferrals'],
        status: draft.status,
        contact_email: draft.contact_email || null,
        endpoint_url: draft.endpoint_url || null,
        notes: draft.notes || null,
      };
      await api.post('/admin/payroll-adapters', payload);
      setMsg(`Adapter '${payload.name}' registered.`);
      setDraft(emptyNew);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  const patchStatus = async (id, status) => {
    setErr(''); setMsg('');
    try {
      await api.patch(`/admin/payroll-adapters/${id}`, { status });
      setMsg('Status updated.');
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Update failed');
    }
  };

  const statusChip = (s) => {
    const map = {
      live: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      contracted: 'bg-sky-50 text-sky-700 border-sky-200',
      provisioning: 'bg-amber-50 text-amber-700 border-amber-200',
      pending_legal_review: 'bg-slate-50 text-slate-700 border-slate-200',
      draft: 'bg-slate-50 text-slate-600 border-slate-200',
      suspended: 'bg-red-50 text-red-700 border-red-200',
      deprecated: 'bg-neutral-100 text-neutral-600 border-neutral-300',
    };
    return <span className={`text-[10px] font-mono px-1.5 py-0.5 border rounded ${map[s] || ''}`}>{s.replace(/_/g, ' ')}</span>;
  };

  return (
    <section className="panel p-5" data-testid="payroll-adapters-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="overline">Payroll Adapter Registry</span>
          <p className="text-[11px] text-[#6B7280] mt-1">
            Catalog of federal (DFAS / NFC / GSA), state, county, and municipal payroll integrations.
          </p>
        </div>
        <Plug size={14} className="text-[#9CA3AF]" />
      </div>

      {/* Summary chips */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3" data-testid="payroll-summary">
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded px-3 py-2">
            <div className="text-[10px] text-[#9CA3AF] uppercase">Total</div>
            <div className="text-base font-mono font-semibold text-[#1B2A4A]">{summary.total}</div>
          </div>
          {['federal', 'state', 'county', 'municipal'].map((k) => (
            <div key={k} className="bg-[#F8F9FA] border border-[#E2E5E9] rounded px-3 py-2">
              <div className="text-[10px] text-[#9CA3AF] uppercase">{k}</div>
              <div className="text-base font-mono font-semibold text-[#1B2A4A]">{summary.by_provider_kind?.[k] ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3" data-testid="payroll-err">{err}</div>}
      {msg && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 mb-3" data-testid="payroll-msg">{msg}</div>}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value)}
          className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
          data-testid="payroll-filter-kind"
        >
          <option value="">All kinds</option>
          {PAYROLL_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
          data-testid="payroll-filter-status"
        >
          <option value="">All statuses</option>
          {PAYROLL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="text-xs border border-[#D1D5DB] rounded px-2 py-1 inline-flex items-center gap-1">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* List */}
      <div className="overflow-x-auto mb-4" data-testid="payroll-list">
        {loading ? (
          <div className="text-xs text-[#6B7280]"><Loader2 size={12} className="animate-spin inline mr-1" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-[#6B7280]">No adapters registered.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[#9CA3AF] border-b border-[#F0F1F3]">
                <th className="py-1.5">Name</th>
                <th>Kind</th>
                <th>Jurisdiction</th>
                <th>Employers</th>
                <th>Connectivity</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-[#F0F1F3]" data-testid={`payroll-row-${a.id}`}>
                  <td className="py-2">{a.name}</td>
                  <td className="font-mono">{a.provider_kind}</td>
                  <td>{a.jurisdiction || '—'}</td>
                  <td className="font-mono text-[#6B7280]">{(a.employer_ids || []).join(', ') || '—'}</td>
                  <td className="font-mono">{a.connectivity}</td>
                  <td>{statusChip(a.status)}</td>
                  <td>
                    <select
                      value={a.status}
                      onChange={(e) => patchStatus(a.id, e.target.value)}
                      className="text-[10px] border border-[#D1D5DB] rounded px-1 py-0.5"
                      data-testid={`payroll-status-select-${a.id}`}
                    >
                      {PAYROLL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New adapter form */}
      <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3" data-testid="payroll-new-form">
        <div className="text-[11px] font-semibold text-[#1B2A4A] mb-2">Register New Adapter</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            placeholder="Name (e.g. DFAS Payroll)"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
            data-testid="payroll-new-name"
          />
          <select
            value={draft.provider_kind}
            onChange={(e) => setDraft({ ...draft, provider_kind: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
            data-testid="payroll-new-kind"
          >
            {PAYROLL_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input
            placeholder="Jurisdiction (e.g. State of Texas)"
            value={draft.jurisdiction}
            onChange={(e) => setDraft({ ...draft, jurisdiction: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
          />
          <input
            placeholder="Employer IDs (comma-separated)"
            value={draft.employer_ids}
            onChange={(e) => setDraft({ ...draft, employer_ids: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1 font-mono"
            data-testid="payroll-new-employers"
          />
          <select
            value={draft.connectivity}
            onChange={(e) => setDraft({ ...draft, connectivity: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
          >
            {PAYROLL_CONN.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
          >
            {PAYROLL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            placeholder="Record types (deferrals,comp_changes,…)"
            value={draft.supported_record_types}
            onChange={(e) => setDraft({ ...draft, supported_record_types: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1 font-mono"
          />
          <input
            placeholder="Contact email"
            value={draft.contact_email}
            onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
            className="text-xs border border-[#D1D5DB] rounded px-2 py-1"
          />
        </div>
        <div className="mt-2 flex justify-end">
          <button
            onClick={create} disabled={creating}
            className="bg-[#1B2A4A] text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-[#0F1A33] disabled:opacity-40"
            data-testid="payroll-new-submit"
          >
            {creating ? 'Registering…' : 'Register Adapter'}
          </button>
        </div>
      </div>
    </section>
  );
};

// ─── Fund Performance Parity Monitor (admin-only, attestation log) ──
const FundParityMonitor = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attesting, setAttesting] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api
      .get('/admin/fund-parity?history_days=7')
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Unable to load parity data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAttestation = async () => {
    setAttesting(true);
    setErr('');
    try {
      await api.post('/admin/fund-parity/attest');
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Attestation failed.');
    } finally {
      setAttesting(false);
    }
  };

  const overall = data?.overall_status || '—';
  const overallGood = overall === 'In Parity';

  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white p-4 space-y-3" data-testid="fund-parity-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-[#1B2A4A]" />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Fund Performance Parity Monitor</h3>
          <span className="text-[9px] uppercase tracking-wider font-semibold bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
            FedRAMP AU-12
          </span>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span
              data-testid="fund-parity-overall-status"
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${
                overallGood
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : 'bg-red-50 text-red-800 border-red-200'
              }`}
            >
              {overallGood ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
              {overall}
            </span>
          )}
          <button
            data-testid="fund-parity-attest-btn"
            onClick={runAttestation}
            disabled={attesting}
            className="inline-flex items-center gap-1 px-3 py-1 rounded text-[11px] font-semibold text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50"
          >
            {attesting ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            Run Attestation
          </button>
        </div>
      </div>
      <p className="text-[11px] text-[#6B7280]">
        Verifies that every UniFed Core Fund&trade; NAV tracks its legacy TSP
        equivalent within a{' '}
        <span className="font-mono text-[#1B2A4A]">
          {data?.threshold_bps ?? 5} bps
        </span>{' '}
        tolerance. Attestations are persisted for Treasury / FRTIB reconciliation.
      </p>

      {err && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700" data-testid="fund-parity-error">
          <AlertTriangle size={11} /> {err}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : data ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="fund-parity-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-[9px] text-[#6B7280] uppercase tracking-wider">
                  <th className="text-left py-1.5 px-3">UniFed Fund</th>
                  <th className="text-left py-1.5 px-3">Legacy</th>
                  <th className="text-right py-1.5 px-3">UniFed NAV</th>
                  <th className="text-right py-1.5 px-3">Legacy Price</th>
                  <th className="text-right py-1.5 px-3">Divergence (bps)</th>
                  <th className="text-left py-1.5 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.current.map((r) => {
                  const good = r.status === 'In Parity';
                  const key = r.unifed_label.split(' ')[0].toLowerCase();
                  return (
                    <tr key={r.unifed_label} className="border-b border-[#F0F1F3]" data-testid={`fund-parity-row-${key}`}>
                      <td className="py-1.5 px-3 font-semibold text-[#1B2A4A]">{r.unifed_label}</td>
                      <td className="py-1.5 px-3 text-[#6B7280] font-mono">{r.legacy_label}</td>
                      <td className="py-1.5 px-3 text-right font-mono text-[#1B2A4A]">${r.unifed_nav.toFixed(4)}</td>
                      <td className="py-1.5 px-3 text-right font-mono text-[#6B7280]">${r.legacy_price.toFixed(4)}</td>
                      <td className={`py-1.5 px-3 text-right font-mono font-semibold ${
                        good ? 'text-emerald-700' : 'text-red-700'
                      }`}>
                        {r.divergence_bps > 0 ? '+' : ''}{r.divergence_bps.toFixed(2)}
                      </td>
                      <td className="py-1.5 px-3">
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold border ${
                            good
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                              : 'bg-red-50 text-red-800 border-red-200'
                          }`}
                        >
                          {good ? <CheckCircle2 size={9} /> : <AlertTriangle size={9} />}
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data.history && data.history.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280] mb-1">
                Attestation History (last {data.history_days} days)
              </p>
              <div className="divide-y divide-[#F0F1F3] border border-[#E5E7EB] rounded" data-testid="fund-parity-history">
                {data.history.slice(0, 10).map((h) => {
                  const ok = h.overall_status === 'In Parity';
                  return (
                    <div
                      key={h.id}
                      data-testid={`fund-parity-history-${h.id}`}
                      className="flex items-center justify-between px-3 py-1.5 text-[11px]"
                    >
                      <span className="font-mono text-[#1B2A4A]">
                        {new Date(h.checked_at).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                          hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                      <span className="font-mono text-[#6B7280]">
                        max {h.max_divergence_bps.toFixed(2)} bps
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold border ${
                          ok
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : 'bg-red-50 text-red-800 border-red-200'
                        }`}
                      >
                        {ok ? <CheckCircle2 size={9} /> : <AlertTriangle size={9} />}
                        {h.overall_status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <p className="text-[10px] text-[#9CA3AF] italic">
            Threshold: 0.05% (5 bps). Attestation log retained for FedRAMP AU-12
            audit evidence and Treasury / FRTIB reconciliation requests.
          </p>
        </>
      ) : null}
    </div>
  );
};

// ─── Fund Taxonomy Panel (admin-only, shows internal mapping) ──────
const FundTaxonomyPanel = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api
      .get('/admin/fund-taxonomy')
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Unable to load.'));
  }, []);

  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white p-4 space-y-3" data-testid="fund-taxonomy-panel">
      <div className="flex items-center gap-2">
        <FileText size={14} className="text-[#1B2A4A]" />
        <h3 className="text-sm font-semibold text-[#1B2A4A]">Fund Taxonomy Mapping</h3>
        <span className="text-[9px] uppercase tracking-wider font-semibold bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
          Internal — Admin Only
        </span>
      </div>
      <p className="text-[11px] text-[#6B7280]">
        {data?.family_label} — internal UniFed ↔ legacy identifier mapping. Shown
        only in admin and compliance contexts; never exposed to participants.
      </p>
      {err && (
        <p className="text-[11px] text-red-700" data-testid="fund-taxonomy-error">{err}</p>
      )}
      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="fund-taxonomy-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-[9px] text-[#6B7280] uppercase tracking-wider">
                <th className="text-left py-1.5 px-3">UniFed Label</th>
                <th className="text-left py-1.5 px-3">Index / Benchmark</th>
                <th className="text-left py-1.5 px-3">Legacy Identifier</th>
                <th className="text-left py-1.5 px-3">Internal Code</th>
                <th className="text-left py-1.5 px-3">Pricing Proxy</th>
              </tr>
            </thead>
            <tbody>
              {data.mapping.map((row) => (
                <tr key={row.tsp_code} className="border-b border-[#F0F1F3]" data-testid={`fund-tax-row-${row.tsp_code}`}>
                  <td className="py-1.5 px-3 font-semibold text-[#1B2A4A]">{row.unifed_label}</td>
                  <td className="py-1.5 px-3 text-[#6B7280]">{row.unifed_suffix}</td>
                  <td className="py-1.5 px-3 text-[#6B7280] font-mono">{row.tsp_legacy_label}</td>
                  <td className="py-1.5 px-3 text-[#6B7280] font-mono uppercase">{row.tsp_code}</td>
                  <td className="py-1.5 px-3 text-[#6B7280] font-mono">{row.proxy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.note && (
        <p className="text-[10px] text-[#9CA3AF] italic">{data.note}</p>
      )}
    </div>
  );
};

// ─── Integration Status Panel ──────────────────────────────────
const IntegrationStatusPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const r = await api.get('/admin/integrations');
      setItems(r.data || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unable to load integrations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="panel p-5" data-testid="integration-status-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Plug size={14} className="text-[#1B2A4A]" />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Integration Status</h3>
        </div>
        <button
          data-testid="integrations-refresh"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-[#D1D5DB] text-[#6B7280] hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={10} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {err && (
        <div className="flex items-center gap-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2">
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      {loading && !items.length ? (
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="integrations-grid">
          {items.map(it => {
            const meta = STATUS_META[it.status] || STATUS_META.Pending;
            const Icon = meta.icon;
            return (
              <div key={it.name} className="rounded border border-[#E5E7EB] bg-white p-4" data-testid={`integration-${it.name.replace(/[^A-Za-z0-9]/g, '-')}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-[#1B2A4A]">{it.name}</h4>
                    {it.subLabel && (
                      <p className="text-[11px] text-[#1B2A4A]/80 leading-relaxed mt-0.5 italic" data-testid="integration-sub-label">
                        {it.subLabel}
                      </p>
                    )}
                    {it.description && (
                      <p className="text-[11px] text-[#6B7280] leading-relaxed mt-0.5">{it.description}</p>
                    )}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border ${meta.cls}`}
                    data-testid="integration-status"
                  >
                    <Icon size={10} />{it.status}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                  <div>
                    <span className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280] block">Last Sync</span>
                    <span className="font-mono text-[#1B2A4A]">{fmtTS(it.lastSync)}</span>
                  </div>
                  {it.atoExpiration && (
                    <div>
                      <span className="text-[9px] uppercase tracking-wider font-semibold text-[#6B7280] block">ATO Expiration</span>
                      <span className="font-mono text-[#1B2A4A]" data-testid="integration-ato">{fmtDate(it.atoExpiration)}</span>
                    </div>
                  )}
                </div>

                {it.errorDetail && (
                  <div className="mt-2 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    <AlertTriangle size={11} className="text-amber-600 mt-0.5 shrink-0" />
                    <span className="text-[10px] text-amber-800 leading-relaxed">{it.errorDetail}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Compliance Checklist ──────────────────────────────────────
const ComplianceChecklist = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/compliance-status'); setItems(r.data || []); }
    finally { setLoading(false); }
  };

  const stats = useMemo(() => {
    const pass = items.filter(i => i.status === 'Pass').length;
    const inP = items.filter(i => i.status === 'In Progress').length;
    const fail = items.filter(i => i.status === 'Fail').length;
    return { pass, inP, fail, total: items.length };
  }, [items]);

  return (
    <div className="panel p-5" data-testid="compliance-checklist-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-[#1B2A4A]" />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Regulatory Compliance Checklist</h3>
        </div>
        {!loading && (
          <div className="flex items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="checklist-pass-count">
              <CheckCircle2 size={10} /> {stats.pass} Pass
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200" data-testid="checklist-progress-count">
              <Clock size={10} /> {stats.inP} In Progress
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-red-50 text-red-800 border-red-200" data-testid="checklist-fail-count">
              <XCircle size={10} /> {stats.fail} Fail
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2" data-testid="checklist-items">
          {items.map((item, i) => {
            const meta = STATUS_META[item.status] || STATUS_META.Pending;
            const Icon = meta.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded border border-[#E5E7EB] bg-white" data-testid={`checklist-item-${i}`}>
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border shrink-0 ${meta.cls}`} data-testid="checklist-status">
                  <Icon size={10} />{item.status}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[#1B2A4A]">{item.requirement}</div>
                  {item.detail && <div className="text-[11px] text-[#6B7280] leading-relaxed mt-0.5">{item.detail}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Audit Trail Table ─────────────────────────────────────────
const AuditTrail = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);

  const [filters, setFilters] = useState({
    from: '', to: '', type: '', participantId: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.type) params.type = filters.type;
      if (filters.participantId) params.participantId = filters.participantId;
      const r = await api.get('/admin/audit-log', { params });
      setRows(r.data?.results || []);
      setTotal(r.data?.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/audit-log/event-types');
        setEventTypes(r.data?.eventTypes || []);
      } catch { setEventTypes([]); }
    })();
  }, []);

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  const applyFilters = () => { setPage(1); load(); };
  const clearFilters = () => { setFilters({ from: '', to: '', type: '', participantId: '' }); setPage(1); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.type) params.set('type', filters.type);
      if (filters.participantId) params.set('participantId', filters.participantId);

      const resp = await api.get(`/admin/audit-log/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const inputCls = "h-8 px-2 bg-white border border-[#D1D5DB] rounded text-[11px] text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A]";

  return (
    <div className="panel p-5" data-testid="audit-trail-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-[#1B2A4A]" />
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Audit Trail</h3>
          <span className="text-[10px] text-[#9CA3AF] font-mono" data-testid="audit-total">{total.toLocaleString()} events</span>
        </div>
        <button
          data-testid="audit-export-csv"
          onClick={exportCsv}
          disabled={exporting}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded bg-[#1B2A4A] text-white hover:bg-[#2D3F5F] disabled:opacity-50"
        >
          {exporting ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-3" data-testid="audit-filters">
        <div>
          <label className="block text-[9px] uppercase tracking-wider font-semibold text-[#6B7280] mb-0.5">From</label>
          <input
            data-testid="audit-filter-from"
            type="datetime-local"
            value={filters.from}
            onChange={e => setFilters({ ...filters, from: e.target.value })}
            className={`${inputCls} w-full`}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider font-semibold text-[#6B7280] mb-0.5">To</label>
          <input
            data-testid="audit-filter-to"
            type="datetime-local"
            value={filters.to}
            onChange={e => setFilters({ ...filters, to: e.target.value })}
            className={`${inputCls} w-full`}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider font-semibold text-[#6B7280] mb-0.5">Event Type</label>
          <select
            data-testid="audit-filter-type"
            value={filters.type}
            onChange={e => setFilters({ ...filters, type: e.target.value })}
            className={`${inputCls} w-full`}
          >
            <option value="">All Types</option>
            {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-wider font-semibold text-[#6B7280] mb-0.5">Participant ID</label>
          <input
            data-testid="audit-filter-participant"
            type="text"
            placeholder="participant-uuid"
            value={filters.participantId}
            onChange={e => setFilters({ ...filters, participantId: e.target.value })}
            className={`${inputCls} w-full`}
          />
        </div>
        <div className="flex items-end gap-2">
          <button
            data-testid="audit-apply-filters"
            onClick={applyFilters}
            className="flex-1 h-8 px-2 bg-[#1B2A4A] text-white text-[11px] rounded hover:bg-[#2D3F5F] flex items-center justify-center gap-1"
          >
            <Search size={10} /> Apply
          </button>
          <button
            data-testid="audit-clear-filters"
            onClick={clearFilters}
            className="flex-1 h-8 px-2 bg-white border border-[#D1D5DB] text-[#6B7280] text-[11px] rounded hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-[#E5E7EB] rounded">
        <table className="w-full text-[11px]" data-testid="audit-table">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left text-[#6B7280]">
              <th className="py-2 px-3 font-medium">Timestamp</th>
              <th className="py-2 px-3 font-medium">Event Type</th>
              <th className="py-2 px-3 font-medium">Description</th>
              <th className="py-2 px-3 font-medium">Participant ID</th>
              <th className="py-2 px-3 font-medium">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-8 text-center text-[#6B7280]">
                <Loader2 size={14} className="inline animate-spin mr-2" /> Loading…
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="py-8 text-center text-[#6B7280]" data-testid="audit-empty">
                No audit events match the current filters.
              </td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]" data-testid={`audit-row-${r.id}`}>
                <td className="py-1.5 px-3 font-mono text-[#6B7280] whitespace-nowrap">{fmtTS(r.timestamp)}</td>
                <td className="py-1.5 px-3">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-[#F0F1F3] text-[10px] font-mono font-medium text-[#1B2A4A]" data-testid="audit-event-type">
                    {r.eventType || '—'}
                  </span>
                </td>
                <td className="py-1.5 px-3 text-[#1B2A4A] max-w-[400px] truncate" title={r.description}>{r.description || '—'}</td>
                <td className="py-1.5 px-3 font-mono text-[#6B7280] text-[10px]">{r.participantId ? r.participantId.slice(0, 8) + '…' : '—'}</td>
                <td className="py-1.5 px-3 font-mono text-[#6B7280]">{r.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between text-[11px]" data-testid="audit-pagination">
        <span className="text-[#6B7280] font-mono">
          Page {page} of {totalPages} · {pageSize} per page
        </span>
        <div className="flex items-center gap-1">
          <button
            data-testid="audit-prev-page"
            onClick={() => setPage(p => Math.max(p - 1, 1))}
            disabled={page <= 1 || loading}
            className="h-7 px-2 border border-[#D1D5DB] rounded text-[#6B7280] hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft size={11} /> Prev
          </button>
          <button
            data-testid="audit-next-page"
            onClick={() => setPage(p => Math.min(p + 1, totalPages))}
            disabled={page >= totalPages || loading}
            className="h-7 px-2 border border-[#D1D5DB] rounded text-[#6B7280] hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
          >
            Next <ChevronRight size={11} />
          </button>
        </div>
      </div>
    </div>
  );
};
